import {
  Badge,
  Button,
  Center, Flex,
  IconButton,
  Menu,
  MenuButton, MenuDivider,
  MenuGroup, MenuItem,
  MenuList,
  Text, Tooltip,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { DeleteDialog } from "../DeleteDialog";
import { DataTable } from "../../../../components/DataTable";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SuppliersPlanningModal } from "./components";
import {
  supplierQuestionnaireKeys,
  useBulkCreateSupplierQuestionnaires,
  useCreateSupplierQuestionnaire,
  useDeleteSupplierQuestionnaire,
  useGetAllSupplierQuestionnaires,
  useUnfinaliseSupplierQuestionnaire,
  useUpdateSupplierQuestionnaire
} from "../../../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import {
  calculateDataTableState,
  constructQueryParamsString,
  formatDate,
  getSortDirection,
  handlePageSizeChange,
  QUARTERS,
  QUESTIONNAIRE_STATUSES,
  showErrorToast,
  showSuccessToast,
  SORT_DIRECTIONS,
  SUPPLIER_QUESTIONNAIRE_TYPES
} from "../../../../utils";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { FaPen, FaTrash, FaEye, FaBars, FaUndo } from "react-icons/fa";
import { SuppliersDataModal } from "../../../Reports/SupplierReports/components/SuppliersDataReport/components";
import { useGetSupplierPhysicalRiskQuestionnaire } from "../../../../hooks/query/supplierPhysicalRiskQuestionnaire";
import PhysicalRiskLocationsModal from "../../../PhysicalRisk/components/PhysicalRiskLocationsModal";
import { MultiSelectFilter } from "../../../../components/DataTable/components";
import { Page } from "../../../../components/Page";
import { Card } from "../../../../components/Card";
import { WithAuthorization } from "../../../../context";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../../config";
import { QUESTIONNAIRE_TYPES } from "../../../SupplierQuestionnaires/helpers";
import { useGetSupplierQuestionnairesSummary } from "../../../../hooks/query/dashboard";

function SuppliersPlanning() {
  const { t } = useTranslation("translation", {
    keyPrefix: "dataEntryPlanning"
  });
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure();
  const {
    isOpen: isSuppliersQuestionnaireOpen,
    onOpen: onSuppliersQuestionnaireOpen,
    onClose: onSuppliersQuestionnaireClose
  } = useDisclosure();
  const {
    isOpen: isPhysicalRiskQuestionnaireOpen,
    onOpen: onPhysicalRiskQuestionnaireOpen,
    onClose: onPhysicalRiskQuestionnaireClose
  } = useDisclosure();

  const [createEditModalData, setCreateEditModalData] = useState(undefined);
  const [deleteModalData, setDeleteModalData] = useState(undefined);

  const [tasksList, setTasksList] = useState([]);
  const [summary, setSummary] = useState(null);

  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    queryPageSortBy: [`id,${SORT_DIRECTIONS.DESC}`],
    totalElements: 0,
    totalPages: 0,
    filterObj: {}
  });

  const { totalPages, filterObj, queryPageIndex, queryPageSize, queryPageSortBy, totalElements } = dataTableState;

  const manageDataTableState = (newValObject) => {
    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...calculateDataTableState({
          prevState,
          newValObject,
          sortAccessors: [
            { originalAccessor: "assigneeName", alternativeAccessor: "user.email" },
            { originalAccessor: "supplier", alternativeAccessor: "supplier.name" },
            { originalAccessor: "ghgStatus", alternativeAccessor: "status" },
            { originalAccessor: "year", alternativeAccessor: "period.year" },
            { originalAccessor: "quarter", alternativeAccessor: "period.quarter" }
          ],
          initialSortingParam: "id"
        })
      };
    });
  };

  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState("");
  const [selectedPhysicalRiskId, setSelectedPhysicalRiskId] = useState("");
  const [physicalRiskModalData, setPhysicalRiskModalData] = useState("");

  const handleErrors = (error) => {
    let errorMsg = error.response?.data?.parameters?.message ?? error.message;
    if (error.response?.status === 409) {
      const resourceName = error.response?.data?.parameters?.resourceName ?? "";
      let typeOfQuestionnaire;

      switch (resourceName) {
        case "SupplierQuestionnaire": {
          typeOfQuestionnaire = t("page.supplierEmissions");
          break;
        }
        case "SupplierQuestionnairePhysicalRisk": {
          typeOfQuestionnaire = t("page.supplierPhysicalRisk");
          break;
        }
        default: {
          typeOfQuestionnaire = "";
        }
      }

      errorMsg = t("page.duplicatedAssignee", { typeOfQuestionnaire: typeOfQuestionnaire });
    }
    dispatch(hideAppSpinner());
    showErrorToast(toast, errorMsg);
  };

  const getQueryParamsString = (isTableDataQuery = false) => {
    let queryParams = {
      ...filterObj
    };

    if (isTableDataQuery) {
      queryParams = {
        ...queryParams,
        page: queryPageIndex,
        size: queryPageSize,
        sort: queryPageSortBy
      };
    }

    return constructQueryParamsString(queryParams, [
      { defaultAttribute: "ghgStatus", multipleValuesAttribute: "ghgStatus" },
      { defaultAttribute: "quarter", multipleValuesAttribute: "quarter" },
      { defaultAttribute: "questionnaireType", multipleValuesAttribute: "questionnaireType" }
    ]);
  };

  useGetSupplierQuestionnairesSummary(getQueryParamsString(), {
    onSuccess: (data) => setSummary(data),
    onError: err => handleErrors(err),
    cacheTime: 0
  });

  const {
    isLoading: isLoadingTasks,
    isFetching: isFetchingTasks
  } = useGetAllSupplierQuestionnaires(getQueryParamsString(true),
    {
      onSuccess: (data) => {
        const pageContent = data?.content ?? [];
        const tasks = pageContent.map((task) => {
          return {
            id: task?.id,
            description: task?.description,
            supplierId: task?.supplier?.id,
            supplier: task?.supplier?.name,
            assigneeId: task?.assigneeId,
            assigneeName: task?.assigneeName ?? task?.inviteeEmail,
            inviteeEmail: task?.inviteeEmail,
            lastModifiedDate: formatDate(task?.lastModifiedDate ?? null),
            year: task?.period?.year?.toString() ?? "",
            quarter: task?.period?.quarter ?? "",
            ghgStatus: t("status." + task?.ghgStatus),
            originalGhgStatus: task?.ghgStatus,
            originalPhysicalRiskStatus: task?.physicalRiskStatus,
            updatable: task?.updatable,
            physicalRiskId: task?.physicalRiskId,
            questionnaireType: t(
              `page.questionnaireTypes.${task?.questionnaireType}`,
              { defaultValue: task?.questionnaireType }
            ),
            originalQuestionnaireType: task?.questionnaireType,
            endDate: formatDate(task?.endDate ?? null, false),
            originalEndDate: task?.endDate ?? null
          };
        });
        setTasksList(tasks);
        handlePageSizeChange(data, queryPageIndex, manageDataTableState);
      },
      onError: err => handleErrors(err),
      cacheTime: 0
    }
  );

  useGetSupplierPhysicalRiskQuestionnaire(selectedPhysicalRiskId, {
    onSuccess: (data) => {
      setPhysicalRiskModalData(data);
      onPhysicalRiskQuestionnaireOpen();
    },
    onError: err => handleErrors(err),
    onSettled: () => dispatch(hideAppSpinner()),
    enabled: selectedPhysicalRiskId !== "",
    cacheTime: 0
  });

  const { mutate: unfinaliseQuestionnaire } = useUnfinaliseSupplierQuestionnaire({
    onMutate: () => {
      dispatch(showAppSpinner());
    },
    onSuccess: () => {
      showSuccessToast(toast, t("messages.unfinaliseSuccess"));
      queryClient.invalidateQueries(supplierQuestionnaireKeys.all());
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const columns = useMemo(() => {
      function handleEdit(data) {
        setCreateEditModalData(data.row.original);
        onOpen();
      }

      function handleDelete(recordId) {
        setDeleteModalData(recordId);
        onDeleteDialogOpen();
      }

      function handleViewQuestionnaire(recordId) {
        setSelectedQuestionnaireId(recordId);
        onSuppliersQuestionnaireOpen();
      }

      function handleEditQuestionnaire(questionnaireId) {
        navigate(`${routes.supplierQuestionnaire}?id=${questionnaireId}`);
      }

      function handleViewPhysicalRisk(physicalRiskId) {
        dispatch(showAppSpinner());
        setSelectedPhysicalRiskId(physicalRiskId);
      }

      function handleEditPhysicalRiskQuestionnaire(questionnaireId) {
        navigate(`${routes.supplierPhysicalRiskQuestionnaire}?id=${questionnaireId}`);
      }

      function handleUnfinaliseQuestionnaire(questionnaireId, type) {
        unfinaliseQuestionnaire({ questionnaireId, type });
      }

      return [
        {
          header: t("page.suppliersPlanningHeaders.supplier"),
          accessorKey: "supplier",
          size: 145,
          sortDirection: getSortDirection(queryPageSortBy, "supplier.name")
        },
        {
          header: t("page.suppliersPlanningHeaders.assignee"),
          accessorKey: "assigneeName",
          size: 145,
          sortDirection: getSortDirection(queryPageSortBy, "user.email")
        },
        {
          header: t("page.suppliersPlanningHeaders.status"),
          accessorKey: "ghgStatus",
          size: 190,
          sortDirection: getSortDirection(queryPageSortBy, "status"),
          filter: {
            params: {
              options: [
                {
                  label: t(`status.${QUESTIONNAIRE_STATUSES.FINALIZED}`),
                  value: QUESTIONNAIRE_STATUSES.FINALIZED
                },
                {
                  label: t(`status.${QUESTIONNAIRE_STATUSES.IN_PROGRESS}`),
                  value: QUESTIONNAIRE_STATUSES.IN_PROGRESS
                },
                {
                  label: t(`status.${QUESTIONNAIRE_STATUSES.NEW}`),
                  value: QUESTIONNAIRE_STATUSES.NEW
                }
              ],
              filterObj,
              setFilterObj: (newFilterObj) => manageDataTableState({
                  filterObj: newFilterObj,
                  queryPageIndex: 0
                }
              ),
              isClearable: false
            },
            component: MultiSelectFilter
          }
        },
        {
          header: t("page.suppliersPlanningHeaders.endDate"),
          accessorKey: "endDate",
          size: 140,
          enableColumnFilter: false,
          sortDirection: getSortDirection(queryPageSortBy, "endDate")
        },
        {
          header: t("page.suppliersPlanningHeaders.type"),
          accessorKey: "questionnaireType",
          size: 210,
          sortDirection: getSortDirection(queryPageSortBy, "questionnaireType"),
          filter: {
            params: {
              options: [
                {
                  label: t(`page.questionnaireTypes.${SUPPLIER_QUESTIONNAIRE_TYPES.EMISSION_BASED}`),
                  value: SUPPLIER_QUESTIONNAIRE_TYPES.EMISSION_BASED
                },
                {
                  label: t(`page.questionnaireTypes.${SUPPLIER_QUESTIONNAIRE_TYPES.CONSUMPTION_BASED}`),
                  value: SUPPLIER_QUESTIONNAIRE_TYPES.CONSUMPTION_BASED
                },
                {
                  label: t(`page.questionnaireTypes.${SUPPLIER_QUESTIONNAIRE_TYPES.DECARBONIZATION_NET_ZERO_PLAN}`),
                  value: SUPPLIER_QUESTIONNAIRE_TYPES.DECARBONIZATION_NET_ZERO_PLAN
                }
              ],
              filterObj,
              setFilterObj: (newFilterObj) => manageDataTableState({
                  filterObj: newFilterObj,
                  queryPageIndex: 0
                }
              ),
              isClearable: false
            },
            component: MultiSelectFilter
          }
        },
        {
          header: t("page.suppliersPlanningHeaders.year"),
          accessorKey: "year",
          size: 130,
          sortDirection: getSortDirection(queryPageSortBy, "period.year")
        },
        {
          header: t("page.suppliersPlanningHeaders.quarter"),
          accessorKey: "quarter",
          size: 140,
          sortDirection: getSortDirection(queryPageSortBy, "period.quarter"),
          filter: {
            params: {
              options: Object.keys(QUARTERS).map(key => {
                return {
                  label: key,
                  value: key
                };
              }),
              filterObj,
              setFilterObj: (newFilterObj) => manageDataTableState({
                  filterObj: newFilterObj,
                  queryPageIndex: 0
                }
              ),
              isClearable: false
            },
            component: MultiSelectFilter
          }
        },
        {
          header: t("page.suppliersPlanningHeaders.description"),
          accessorKey: "description",
          size: 120,
          enableColumnFilter: false,
          isSortable: false
        },
        {
          header: t("page.suppliersPlanningHeaders.lastModified"),
          accessorKey: "lastModifiedDate",
          size: 140,
          enableColumnFilter: false,
          sortDirection: getSortDirection(queryPageSortBy, "lastModifiedDate")
        },
        {
          header: <div style={{ textAlign: "left" }}><b>{t("page.suppliersPlanningHeaders.actions")}</b></div>,
          accessorKey: "action",
          size: 70,
          enableColumnFilter: false,
          isSortable: false,
          cell: row => (
            <Menu isLazy strategy="fixed">
              <MenuButton
                as={IconButton}
                aria-label="Actions"
                icon={<FaBars />}
                variant="outline"
              />
              <MenuList>
                <MenuGroup title={t("page.manageTaskGroupHeader")}>
                  <MenuItem
                    icon={<FaPen color="green" opacity="0.7" />}
                    onClick={() => handleEdit(row)}
                    isDisabled={!row.row.original.updatable}
                  >
                    {t("page.editTask")}
                  </MenuItem>
                  <MenuItem
                    icon={<FaTrash color="red" opacity="0.6" />}
                    onClick={() => handleDelete(row.row.original.id)}
                  >
                    {t("page.deleteTask")}
                  </MenuItem>
                </MenuGroup>
                <MenuDivider />
                <MenuGroup title={t("page.manageDataGroupHeader")}>
                  {row.row.original.originalQuestionnaireType !== QUESTIONNAIRE_TYPES.DECARBONIZATION_NET_ZERO_PLAN ?
                    <>
                      <MenuItem
                        icon={<FaEye color="green" opacity="0.7" />}
                        onClick={() => handleViewQuestionnaire(row.row.original.id)}
                      >
                        {t("page.viewEmissionsData")}
                      </MenuItem>
                      <MenuItem
                        icon={<FaPen color="green" opacity="0.7" />}
                        onClick={() => handleEditQuestionnaire(row.row.original.id)}
                        isDisabled={row.row.original.originalGhgStatus === QUESTIONNAIRE_STATUSES.FINALIZED}
                        title={row.row.original.originalGhgStatus === QUESTIONNAIRE_STATUSES.FINALIZED ?
                          t("page.finalisedQuestionnaireTooltip") : null}
                      >
                        {t("page.editEmissionsData")}
                      </MenuItem>
                      <MenuItem
                        icon={<FaUndo color="green" opacity="0.7" />}
                        onClick={() => handleUnfinaliseQuestionnaire(
                          row.row.original.id, QUESTIONNAIRE_TYPES.GHG_EMISSIONS
                        )}
                        isDisabled={row.row.original.originalGhgStatus !== QUESTIONNAIRE_STATUSES.FINALIZED}
                      >
                        {t("page.unfinaliseGHGQuestionnaire")}
                      </MenuItem>
                      {typeof row.row.original.physicalRiskId !== "undefined" && row.row.original.physicalRiskId !== null ?
                        <>
                          <MenuItem
                            icon={<FaEye color="blue" opacity="0.7" />}
                            onClick={() => handleViewPhysicalRisk(row.row.original.physicalRiskId)}
                          >
                            {t("page.viewSuppliersPhysicalRisk")}
                          </MenuItem>
                          <MenuItem
                            icon={<FaPen color="blue" opacity="0.7" />}
                            onClick={() => handleEditPhysicalRiskQuestionnaire(row.row.original.physicalRiskId)}
                            isDisabled={row.row.original.originalPhysicalRiskStatus === QUESTIONNAIRE_STATUSES.FINALIZED}
                            title={row.row.original.originalPhysicalRiskStatus === QUESTIONNAIRE_STATUSES.FINALIZED ?
                              t("page.finalisedQuestionnaireTooltip") : null}
                          >
                            {t("page.editSuppliersPhysicalRisk")}
                          </MenuItem>
                          <MenuItem
                            icon={<FaUndo color="blue" opacity="0.7" />}
                            onClick={() => handleUnfinaliseQuestionnaire(
                              row.row.original.physicalRiskId, QUESTIONNAIRE_TYPES.PHYSICAL_RISK
                            )}
                            isDisabled={row.row.original.originalPhysicalRiskStatus !== QUESTIONNAIRE_STATUSES.FINALIZED}
                          >
                            {t("page.unfinalisePhysicalRiskQuestionnaire")}
                          </MenuItem>
                        </> : null
                      }
                    </> :
                    <>
                      <MenuItem
                        icon={<FaEye color="#df7519" />}
                        onClick={() => handleViewQuestionnaire(row.row.original.id)}
                      >
                        {t("page.viewDecarbPlan")}
                      </MenuItem>
                      <MenuItem
                        icon={<FaPen color="#df7519" />}
                        onClick={() => handleEditQuestionnaire(row.row.original.id)}
                        isDisabled={row.row.original.originalGhgStatus === QUESTIONNAIRE_STATUSES.FINALIZED}
                        title={row.row.original.originalGhgStatus === QUESTIONNAIRE_STATUSES.FINALIZED ?
                          t("page.finalisedQuestionnaireTooltip") : null}
                      >
                        {t("page.editDecarbPlan")}
                      </MenuItem>
                      <MenuItem
                        icon={<FaUndo color="#df7519" />}
                        onClick={() => handleUnfinaliseQuestionnaire(
                          row.row.original.id, QUESTIONNAIRE_TYPES.DECARBONIZATION_NET_ZERO_PLAN
                        )}
                        isDisabled={row.row.original.originalGhgStatus !== QUESTIONNAIRE_STATUSES.FINALIZED}
                      >
                        {t("page.unfinaliseDecarbPlanQuestionnaire")}
                      </MenuItem>
                    </>
                  }
                </MenuGroup>
              </MenuList>
            </Menu>
          )
        }
      ];
    }, [onDeleteDialogOpen, onOpen, onSuppliersQuestionnaireOpen, dispatch, t, queryPageSortBy, filterObj,
      navigate, unfinaliseQuestionnaire]
  );

  function addNewTask() {
    onOpen();
  }

  function closeCreateEditModal() {
    setCreateEditModalData(undefined);
    onClose();
  }

  const closeSuppliersQuestionnaireModal = () => {
    setSelectedQuestionnaireId("");
    onSuppliersQuestionnaireClose();
  };

  const closePhysicalRiskQuestionnaireModal = () => {
    setPhysicalRiskModalData(undefined);
    setSelectedPhysicalRiskId("");
    onPhysicalRiskQuestionnaireClose();
  };

  const { mutate: createTask } = useCreateSupplierQuestionnaire({
    onSuccess: () => {
      closeCreateEditModal();
      showSuccessToast(toast, t("messages.taskCreated"));
      queryClient.invalidateQueries(supplierQuestionnaireKeys.all());
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutate: bulkCreateQuestionnaires } = useBulkCreateSupplierQuestionnaires({
    onSuccess: () => {
      closeCreateEditModal();
      showSuccessToast(toast, t("messages.tasksCreated"));
      queryClient.invalidateQueries(supplierQuestionnaireKeys.all());
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutate: updateTask } = useUpdateSupplierQuestionnaire({
    onSuccess: () => {
      closeCreateEditModal();
      showSuccessToast(toast, t("messages.taskUpdated"));
      queryClient.invalidateQueries(supplierQuestionnaireKeys.all());
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const onModalSaved = (id, data) => {
    dispatch(showAppSpinner());

    if (id !== undefined) {
      updateTask({ id, data });
    } else {
      !data.supplier ? bulkCreateQuestionnaires(data) : createTask(data);
    }
  };

  const { mutate: deleteTask } = useDeleteSupplierQuestionnaire({
    onSuccess: () => {
      onDeleteDialogClose();
      showSuccessToast(toast, t("messages.taskDeleted"));
      queryClient.invalidateQueries(supplierQuestionnaireKeys.all());
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteTask(deleteModalData);
  };

  const handleSummaryBadgeClick = (statusValue) => {
    manageDataTableState({
      filterObj: {
        attribute: "ghgStatus",
        value: [{ label: t(`status.${statusValue}`), value: statusValue }]
      },
      queryPageIndex: 0
    });
  };

  return <Page title={t("page.title")} subTitle={t("page.subTitle")}>
    <Card>
      <Center marginBlockEnd="5">
        <Tooltip label={t("page.addNewTask.tooltip")}>
          <Button
            colorScheme="customScheme"
            width="40"
            onClick={addNewTask}
          >
            + {t("page.addNewTask.title")}
          </Button>
        </Tooltip>
      </Center>

      {isOpen && <SuppliersPlanningModal
        modalData={createEditModalData}
        isOpen={isOpen}
        onClose={closeCreateEditModal}
        onSubmit={onModalSaved}
      />}

      <DeleteDialog
        isOpen={isConfirmDialogOpen}
        onClose={onDeleteDialogClose}
        onDelete={deleteRecord}
        entityName={"Task"}
      />

      <SuppliersDataModal
        questionnaireId={selectedQuestionnaireId}
        isOpen={isSuppliersQuestionnaireOpen}
        onClose={closeSuppliersQuestionnaireModal}
      />

      <PhysicalRiskLocationsModal
        modalData={physicalRiskModalData}
        isOpen={isPhysicalRiskQuestionnaireOpen}
        onClose={closePhysicalRiskQuestionnaireModal}
      />

      <Flex alignItems="center" marginBottom={3} gap={3}>
        <Text paddingStart={4}>{t("page.summaryText")}</Text>
        <Badge
          backgroundColor={"esg_blue"}
          color="white"
          height="max-content"
          cursor="pointer"
          onClick={() => handleSummaryBadgeClick(QUESTIONNAIRE_STATUSES.NEW)}
        >
          {`${summary?.newCount ?? "n/a"} ${t("status.NEW")}`}
        </Badge>
        <Badge
          backgroundColor={"esg_orange"}
          color="white"
          height="max-content"
          cursor="pointer"
          onClick={() => handleSummaryBadgeClick(QUESTIONNAIRE_STATUSES.IN_PROGRESS)}
        >
          {`${summary?.inProgressCount ?? "n/a"} ${t("status.IN_PROGRESS")}`}
        </Badge>
        <Badge
          backgroundColor={"green.400"}
          color="white"
          height="max-content"
          cursor="pointer"
          onClick={() => handleSummaryBadgeClick(QUESTIONNAIRE_STATUSES.FINALIZED)}
        >
          {`${summary?.finalizedCount ?? "n/a"} ${t("status.FINALIZED")}`}
        </Badge>
      </Flex>

      <DataTable
        data={tasksList}
        columns={columns}
        isLoading={isLoadingTasks || isFetchingTasks}
        serverSidePagination={true}
        queryTotalCount={totalElements}
        queryPageCount={totalPages}
        queryPageIndex={queryPageIndex}
        setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
        queryPageSize={queryPageSize}
        setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
        queryPageSortBy={queryPageSortBy}
        setQueryPageSortBy={(value) => manageDataTableState({ queryPageSortBy: value })}
        filterObj={filterObj}
        setFilterObj={(newFilterObj) => manageDataTableState({
          filterObj: newFilterObj,
          queryPageIndex: 0
        })}
        enableFilters={true}
        enableSorting={true}
        containerHeight="var(--chakra-sizes-2xl)"
      />
    </Card>
  </Page>;
}

export default WithAuthorization(SuppliersPlanning);